import { resolveApiUrl } from "../../utils/url/urlResolver";
import { type NumberSystem } from "~/HousingSelectionAssistant/types/types";
import { type HSATabularColumnConfig } from "~/HousingSelectionAssistant/redux/types";
import { NUMBER_OF_BOLTS_CODE } from "~/HousingSelectionAssistant/constants";
import { Filter, type TechFilter } from "~/Search/filterConfigModels";
import type { TabularColumnDefDTO } from "~/Search/dto/SearchResultsDTO";

interface Result {
  tabularViewColumnNames: string[];
  tabularViewColumnConfig: HSATabularColumnConfig[];
}

function getTechFilters(groups: FilterGroupDTO[]): TechFilter[] {
  return groups
    .filter((group): group is TechFilterGroupDTO => group.type === Filter.TECH_VALUE)
    .flatMap((group) => group.items)
    .flatMap((category) => category.items)
    .map((techFilterDto) => {
      const ascCodeObj = techFilterDto.sortFields.filter((item) => item.ascending)[0];
      const sortAscCode = ascCodeObj ? ascCodeObj.uid : undefined;
      const descCodeObj = techFilterDto.sortFields.filter((item) => !item.ascending)[0];
      const sortDescCode = descCodeObj ? descCodeObj.uid : undefined;
      return {
        uid: techFilterDto.uid,
        title: `${techFilterDto.name}`,
        unit: `${!techFilterDto.uom ? "" : `(${techFilterDto.uom})`}`,
        assistantLabel: techFilterDto.assistantLabel,
        tabularTitle: techFilterDto.abbreviation,
        sortAscCode,
        sortDescCode,
        description: techFilterDto.description === techFilterDto.name ? "" : techFilterDto.description,
      };
    });
}

export async function getHousingSAFilterConfig(unit: NumberSystem): Promise<Result> {
  const apiUrl = resolveApiUrl(`/search/facets/configuration/HOUSINGS2?isSelectionAssistant=HOUSINGS&unit=${unit}`);

  const response = await fetch(apiUrl, {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
  });
  if (response.redirected) {
    window.location.reload();
  }
  if (!response.ok) {
    throw new Error("Error fetching data");
  }
  const data: Response = await response.json();

  const tabularViewColumnNames = data.featureList.map((feature) => feature.code);
  tabularViewColumnNames.push(NUMBER_OF_BOLTS_CODE);
  const techFilters = getTechFilters(data.solrFacetConfigData.groups);
  const possibleColumns: HSATabularColumnConfig[] = techFilters.map((techFilter) => ({
    name: techFilter.tabularTitle,
    hoverTitle: techFilter.title,
    techValueUID: techFilter.uid,
    sortAsc: techFilter.sortAscCode,
    sortDesc: techFilter.sortDescCode,
    unit: techFilter.unit,
    assistantLabel: techFilter.assistantLabel ?? "",
  }));
  possibleColumns.push({
    name: "page.selectionAssistant.tableHeader.numberOfBolts",
    hoverTitle: "page.selectionAssistant.tableHeader.numberOfBolts",
    techValueUID: NUMBER_OF_BOLTS_CODE,
    assistantLabel: "",
  });
  const tabularViewColumnConfig = possibleColumns.filter((column) =>
    tabularViewColumnNames.includes(column.techValueUID),
  );

  return {
    tabularViewColumnNames,
    tabularViewColumnConfig,
  };
}

interface Response {
  featureList: TabularColumnDefDTO[];
  solrFacetConfigData: {
    groups: FilterGroupDTO[];
  };
}

type FilterGroupDTO = TechFilterGroupDTO | FacetFilterGroupDTO | TaxonomyFilterGroupDTO;

interface TechFilterGroupDTO {
  title: string | null;
  maxElementsVisible: number;
  type: typeof Filter.TECH_VALUE;
  items: TechFilterCategoryDTO[];
}

interface TechFilterCategoryDTO {
  title: string;
  facetId: null;
  columnAmount: number;
  initiallyExpanded: boolean;
  maxElementsVisible: number;
  items: TechFilterDTO[];
  unit?: string;
}

interface TechFilterDTO {
  uid: string;
  name: string;
  assistantLabel: string;
  groupName: string;
  sequenceId: number;
  uom: string;
  sortFields: { uid: string; ascending: boolean }[];
  abbreviation: string;
  description?: string;
}

interface FacetFilterGroupDTO {
  title: string | null;
  maxElementsVisible: number;
  type: typeof Filter.FACET;
  items: FacetFilterDTO[];
}

interface FacetFilterDTO {
  title: string;
  facetId: string;
  columnAmount: number;
  initiallyExpanded: boolean;
}

interface TaxonomyFilterGroupDTO {
  title: string | null;
  type: typeof Filter.TAXONOMY;
  showEmptyCategories: boolean;
}
